import * as ko from 'knockout';

import { ValidationResult } from '../api/request';
import { BaseForm } from '../screens/base_form';
import { DimensionMeta } from '../models/dimension_meta';
import { Dimension, DimensionStage, ScheduledApplication } from '../models/dimension';
import { applyCodeFormatValidation } from '../ko_bindings/slug_validation';
import * as dimensionMetasApi from '../api/dimension_metas';
import * as dimensionsApi from '../api/dimensions';
import * as usersApi from '../api/users';
import { Deferred } from '../utils/deferred';
import { translate } from '../i18n_text';
import { createWithComponent } from '../utils/ko_utils';
import { canEditDimension } from '../permissions';
import i18n from '../i18n';
import { FormNestedEntitiesConfiguration } from './form_nested_entities';
import { getCropSearchConfig } from '../components/configs/search_configs';

let template = require('raw-loader!../../templates/components/base_dimension_record_edit.html').default;

export interface BaseDimensionRecordEditDelegate<
  TData extends dimensionsApi.DimensionData,
  T extends Dimension
> {
  dimensionMetaId: string;
  dimensionId: string;
  dimension: KnockoutObservable<T>;

  disableApplications?: boolean;
  allowAnonymize: boolean;
  allowLinkingCrops: boolean;
  anyoneCanEdit: boolean;
  fetch(dimensionMetaId: string, id: string): Promise<TData>;
  instantiate(data: TData, dimensionMeta: DimensionMeta): T;
  saveRequest(data: TData): Promise<ValidationResult>;
  addAnother?(data: TData, dm: DimensionMeta): void;

  onBeforeSave?(form: BaseForm<TData>): void;
  onRemoteValidation?(form: BaseForm<TData>, result: ValidationResult): void;

  anonymizedCodeText?: string;
  editPermissionCheck?(): boolean;
}

class BaseDimensionRecordEdit<
  TData extends dimensionsApi.DimensionData,
  T extends Dimension
> extends BaseForm<TData> {
  private delegate: BaseDimensionRecordEditDelegate<TData, T>;

  dimensionMeta = ko.observable<DimensionMeta>(null);

  name = ko.pureComputed(() => {
    if (!this.dimensionMeta()) {
      return '';
    }

    return translate(this.dimensionMeta().nameJson());
  });
  canEdit = ko.observable(false);
  anonymizedCodeText: string;

  applications = ko.observableArray<ScheduledApplication>();

  cropSearchConfig = ko.pureComputed(() => {
    return getCropSearchConfig(this.delegate.dimension().crops);
  });

  // Stages live on the dimension model; the section renders only for dimension types
  // whose meta has supports_stages.
  stagesConfig: FormNestedEntitiesConfiguration<DimensionStage> = null;

  applicationsConfig: FormNestedEntitiesConfiguration<ScheduledApplication> = {
    title: i18n.t(['scheduled_applications_colon', 'Scheduled applications:']),
    addTitle: i18n.t('Add scheduled application'),
    missingTitle: i18n.t('No input selected'),

    entities: ko.computed(() => this.applications() ?? []),

    canRemove: () => true,

    add: () => {
      let scApp = new ScheduledApplication();
      this.applications.push(scApp);
      return scApp;
    },

    remove: (entity) => {
      entity.dispose();
      this.applications.remove(entity);
    },

    hasErrors: (entity) => {
      return entity.hasErrors();
    },

    showErrors: (entity) => {
      entity.errors.showAllMessages();
    },

    actions: [],

    getSummaryName: (entity) => {
      let name = '';

      const inputDimension = entity.inputDimension();
      if (inputDimension) {
        name = translate(inputDimension.name_json);

        const dosage = parseFloat(entity.dosage());
        if (!isNaN(dosage)) {
          name += ' ' + dosage.toLocaleString() + ' ';
          name += entity.unit()?.name ?? '';
        }
        const days = parseInt(entity.daysOffset(), 10);
        const mm = entity.baseDate();
        const after = mm ? translate(mm.name_json) : i18n.t('reference date')();
        if (!isNaN(days)) {
          name += `, ${days} ${i18n.t('days after')()} ${after}`;
        }
      }

      return name;
    },
  };

  editText = ko.pureComputed(() => i18n.t('Edit {{ name }}', { name: this.name() })());
  createText = ko.pureComputed(() => i18n.t('Create {{ name }}', { name: this.name() })());

  constructor(
    params: {
      delegate: BaseDimensionRecordEditDelegate<TData, T>;
      result?: Deferred<TData>;
    },
    private componentInfo: KnockoutComponentTypes.ComponentInfo
  ) {
    super(params);

    this.delegate = params.delegate;

    this.anonymizedCodeText =
      params.delegate.anonymizedCodeText || i18n.t('Code to use when partially anonymized')();

    let mePromise = usersApi.me();
    let dimensionMetaPromise = dimensionMetasApi.retrieve(this.delegate.dimensionMetaId);
    let dimensionPromise = this.delegate.dimensionId
      ? this.delegate.fetch(this.delegate.dimensionMetaId, this.delegate.dimensionId)
      : undefined;

    let promise = Promise.all([mePromise, dimensionMetaPromise, dimensionPromise]).then(
      ([userData, dimensionMetaData, dimensionData]) => {
        const canEdit = this.delegate.editPermissionCheck
          ? this.delegate.editPermissionCheck()
          : canEditDimension();
        this.canEdit(this.delegate.anyoneCanEdit || canEdit);
        this.dimensionMeta(new DimensionMeta(dimensionMetaData));
        const dimension = this.delegate.instantiate(dimensionData, this.dimensionMeta());
        dimension.enforceRequiredAttributes(true);
        applyCodeFormatValidation(dimension.code);
        // Set before publishing the dimension: that is what makes the form render.
        this.stagesConfig = dimension.stagesConfig;
        this.delegate.dimension(dimension);

        if (dimensionData?.applications) {
          const applications = dimensionData.applications.slice();
          applications.sort((a, b) => {
            const aDays = parseInt((a.estimated_days_after_planting ?? a.days_offset)?.toString(), 10);
            const bDays = parseInt((b.estimated_days_after_planting ?? b.days_offset)?.toString(), 10);

            if (aDays == null && bDays == null) {
              return 0;
            }
            if (aDays == null) {
              return -1;
            }
            if (bDays == null) {
              return 1;
            }

            return aDays - bDays;
          });
          this.applications(applications.map((data) => new ScheduledApplication(data)));
        }
      }
    );
    this.loadedAfter(promise).then(() => this.focusFirst(componentInfo.element));
  }

  dispose() {
    this.applications().forEach((a) => a.dispose());
  }

  canAddAnother = ko.pureComputed(() => {
    return !this.delegate.dimension().id() && !!this.delegate.addAnother;
  });

  save = () => {
    this.doSave();
  };

  saveAndAddAnother = () => {
    this.doSave((data) => {
      if (this.delegate.addAnother) {
        this.delegate.addAnother(data, this.dimensionMeta());
        this.focusFirst(this.componentInfo.element);
      }
    });
  };

  private doSave(onClose?: (data: TData) => void) {
    if (!this.delegate.dimension()) {
      return;
    }

    for (let attribute of this.delegate.dimension().attributes()) {
      attribute.value.serverError(null);
    }

    for (let stage of this.delegate.dimension().stages()) {
      this.clearModelServerErrors(stage);
    }

    if (this.delegate.onBeforeSave) {
      this.delegate.onBeforeSave(this);
    }

    let scAppError = false;
    for (let scApp of this.applications()) {
      if (scApp.hasErrors()) {
        scApp.showErrors();
        scAppError = true;
      }
    }

    if (!scAppError && this.validateLocal(this.delegate.dimension)) {
      let data = {
        applications: this.applications().map((app) => app.toData()),
        ...this.delegate.dimension().toData(),
      };
      this.executeSaveRequest(this.delegate.saveRequest(<TData>data)).then((validation) => {
        this.onRemoteValidation(<TData>data, this.delegate.dimension(), validation, onClose);

        if (!validation.isValid && validation.errors['stages']) {
          // The server reports stage errors positionally, one entry per submitted row.
          let stagesErrors: any = validation.errors['stages'];
          let stages = this.delegate.dimension().stages();

          for (let i = 0; i < stagesErrors.length && i < stages.length; i++) {
            this.applyModelServerErrors(stages[i], stagesErrors[i]);
          }
        }

        if (this.delegate.onRemoteValidation) {
          this.delegate.onRemoteValidation(this, validation);
        }

        if (!validation.isValid) {
          for (let attribute of this.delegate.dimension().attributes()) {
            let errors = validation.errors[attribute.getAttrName()];

            if (errors) {
              this.setServerError(attribute.value, errors);
            }
          }
        }
      });
    }
  }
}

ko.components.register('base-dimension-record-edit', {
  viewModel: createWithComponent(BaseDimensionRecordEdit),
  template: template,
});
